import { Box, Table } from "@chakra-ui/react";
import { formatNumber } from "../../../store/helpers";
import {
  TOP_SERVICE_CHART_LIMIT,
  buildArrivalRatesPerServiceInsights,
} from "../../../store/arrivalRatesPerServiceInsights";

const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;
const formatDecimal = (value) => Number(value || 0).toFixed(2);

const Kpi = ({ label, value }) => (
  <Box className="arrivalRatesEnterpriseKpi">
    <Box className="arrivalRatesEnterpriseKpiLabel">{label}</Box>
    <Box className="arrivalRatesEnterpriseKpiValue">{value}</Box>
  </Box>
);

const InsightCard = ({ label, service, value }) => (
  <Box className="arrivalRatesEnterpriseInsight">
    <Box className="arrivalRatesEnterpriseKpiLabel">{label}</Box>
    <Box className="arrivalRatesEnterpriseInsightName">{service?.serviceName || "No service"}</Box>
    <Box className="arrivalRatesEnterpriseInsightValue">{value}</Box>
  </Box>
);

const ParetoBar = ({ service }) => (
  <Box className="arrivalRatesParetoRow">
    <Box className="arrivalRatesParetoName">{service.serviceName}</Box>
    <Box className="arrivalRatesParetoTrack">
      <Box
        className="arrivalRatesParetoBar"
        style={{ width: `${Math.min(Number(service.totalPct || 0), 100)}%` }}
      />
    </Box>
    <Box className="arrivalRatesParetoValue">
      {formatNumber(service.total)} / {formatPercent(service.totalPct)}
    </Box>
  </Box>
);

const ArrivalRatesPerServiceSummary = ({ branch }) => {
  const insights = buildArrivalRatesPerServiceInsights(branch);
  const {
    branchTotalArrivals,
    serviceCount,
    highVolatilityCount,
    rankedServices,
    topChartServices,
    topService,
    peakService,
    volatileService,
  } = insights;

  return (
    <Box className="arrivalRatesEnterpriseSummary">
      <h3>Service Demand Summary</h3>
      <Box className="arrivalRatesEnterpriseKpiGrid">
        <Kpi label="Total arrivals" value={formatNumber(branchTotalArrivals)} />
        <Kpi label="Services" value={formatNumber(serviceCount)} />
        <Kpi label="Top service" value={topService?.serviceName || "No service"} />
        <Kpi label="Top service share" value={formatPercent(topService?.totalPct)} />
        <Kpi
          label="Peak day"
          value={peakService?.peakDay ? `Day ${peakService.peakDay} (${formatNumber(peakService.peakValue)})` : "No peak"}
        />
        <Kpi label="High volatility services" value={formatNumber(highVolatilityCount)} />
      </Box>

      <Box className="arrivalRatesEnterpriseInsightGrid">
        <InsightCard
          label="Highest demand"
          service={topService}
          value={`${formatNumber(topService?.total)} arrivals`}
        />
        <InsightCard
          label="Biggest single-day peak"
          service={peakService}
          value={peakService?.peakDay ? `Day ${peakService.peakDay}: ${formatNumber(peakService.peakValue)}` : "No peak"}
        />
        <InsightCard
          label="Most volatile"
          service={volatileService}
          value={`${volatileService?.volatilityBand || "Low"} (${formatDecimal(volatileService?.volatilityScore)})`}
        />
      </Box>

      <Box className="arrivalRatesParetoBlock">
        <h4>Top Service Demand Pareto</h4>
        {topChartServices.slice(0, TOP_SERVICE_CHART_LIMIT).map((service) => (
          <ParetoBar key={service.serviceId || service.serviceName} service={service} />
        ))}
      </Box>

      <Box className="reportTableContainer">
        <Table.Root className="stripedTable reportDataTable reportDataTable--compact" size="sm">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Rank</Table.ColumnHeader>
              <Table.ColumnHeader>Service</Table.ColumnHeader>
              <Table.ColumnHeader>Total</Table.ColumnHeader>
              <Table.ColumnHeader>Demand Share</Table.ColumnHeader>
              <Table.ColumnHeader>Avg / Business Day</Table.ColumnHeader>
              <Table.ColumnHeader>Peak Day</Table.ColumnHeader>
              <Table.ColumnHeader>Peak</Table.ColumnHeader>
              <Table.ColumnHeader>Volatility</Table.ColumnHeader>
              <Table.ColumnHeader>Cumulative</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {rankedServices.map((service) => (
              <Table.Row key={service.serviceId || service.serviceName}>
                <Table.Cell>{service.rank}</Table.Cell>
                <Table.Cell>{service.serviceName}</Table.Cell>
                <Table.Cell>{formatNumber(service.total)}</Table.Cell>
                <Table.Cell>{formatPercent(service.totalPct)}</Table.Cell>
                <Table.Cell>{formatDecimal(service.averagePerBusinessDay)}</Table.Cell>
                <Table.Cell>{service.peakDay ? `Day ${service.peakDay}` : "-"}</Table.Cell>
                <Table.Cell>{formatNumber(service.peakValue)}</Table.Cell>
                <Table.Cell>{service.volatilityBand}</Table.Cell>
                <Table.Cell>{formatPercent(service.cumulativePct)}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Box>
    </Box>
  );
};

export default ArrivalRatesPerServiceSummary;
